import React from "react";
import { Form, Field } from "react-final-form";
import Modal from "../Modal";
import Button from "../Button";
import InputField from "../InputField";
import { useLocalization } from "../../../context/LocalizeContextProvider";
import styles from "./ModalEmailReceipt.module.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Confirmation modal for sending a payment receipt email.
 *
 * Two-mode UI:
 *   - Patient already has an email → show read-only recipient + confirm button.
 *   - Patient has NO email → prompt for one. Server always persists the typed
 *     email to the patient profile on successful send (no opt-in checkbox —
 *     if staff took the time to type it and send with it, they want it saved).
 */
export default function ModalEmailReceipt({
  show = false,
  patientEmail = "",
  patientName = "",
  isLoading = false,
  onCancel = () => undefined,
  onConfirm = () => undefined,
}) {
  const localize = useLocalization();
  const hasExistingEmail = Boolean(patientEmail);

  const validate = (values) => {
    const errors = {};
    if (!hasExistingEmail) {
      if (!values.email || !values.email.trim()) {
        errors.email = localize.getText("required");
      } else if (!EMAIL_RE.test(values.email.trim())) {
        errors.email = localize.getText("invalidEmail");
      } else if (values.email.trim().length > 320) {
        errors.email = localize.getText("invalidEmail");
      }
    }
    return errors;
  };

  const handleSubmit = (values) => {
    const email = hasExistingEmail
      ? patientEmail
      : (values.email || "").trim();
    onConfirm({ email });
  };

  return (
    <Modal centered visible={show} size="sm">
      <div className={styles.wrapper}>
        <h3 className="m-0">{localize.getText("sendReceiptEmailTitle")}</h3>
        <p className={styles.subtitle}>
          {localize.getText("sendReceiptEmailBody")}
          {patientName ? ` (${patientName})` : ""}
        </p>

        <Form onSubmit={handleSubmit} validate={validate}>
          {({ handleSubmit: submit, invalid }) => (
            <form onSubmit={submit} className={styles.form}>
              {hasExistingEmail ? (
                <div
                  className={styles.existingEmail}
                  data-testid="existing-email"
                >
                  <label>{localize.getText("email")}</label>
                  <div className={styles.emailValue}>{patientEmail}</div>
                </div>
              ) : (
                <Field name="email">
                  {({ input, meta }) => (
                    <InputField
                      label={localize.getText("email")}
                      placeholder="patient@example.com"
                      isBorder
                      error={meta.touched && meta.error}
                      onChange={(value) => input.onChange(value)}
                      onBlur={input.onBlur}
                      value={input.value}
                    />
                  )}
                </Field>
              )}

              <div className={styles.actions}>
                <Button
                  label={localize.getText("cancel")}
                  variant="outline-primary"
                  onClick={onCancel}
                  type="button"
                  disabled={isLoading}
                />
                <Button
                  label={localize.getText("send")}
                  variant="warning"
                  type="submit"
                  loading={isLoading}
                  disabled={invalid || isLoading}
                />
              </div>
            </form>
          )}
        </Form>
      </div>
    </Modal>
  );
}
